import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { TablaGeneralStyles } from './styles';
import { responsive } from '../../resources/constantes.json';

/**
 * TablaGen genera una tabla con accesibilidad. Puede crear caption, thead, tbody y tfoot.
 * Atributos parametrizables: colspan, tabindex y th en tbody.
 * Atributos autogenerados: scopes, tags de celda, classnames (hover con rowspans).
 * Por defecto importa el styled TablaGeneralStyles, también atendiendo a la accesibilidad.
 */

const sharedWidth = (styles, idx) => {
  const x = styles?.width?.[idx];
  return x ? x + '%' : '';
};
const sharedAlign = (styles, idx) => styles?.align?.[idx] ?? 'center';
const sharedPadding = (styles, idx) => {
  const padding = styles?.padding?.[idx];
  return padding ? padding.toString().replace(',', 'rem ') + 'rem' : '';
};

const Thead = ({ head, assets }) => (
  <thead>
    {head.map((row, i) => (
      <tr key={i}>
        {row.headers.map((celda, j) => {
          const TagCelda = celda !== '' ? 'th' : 'td';
          return (
            <CeldaStyled
              key={j}
              colSpan={row.colSpan?.[j]}
              index={j}
              assets={assets}
              as={TagCelda}
              scope={
                assets.hasOwnProperty('primary') && TagCelda === 'th'
                  ? 'col'
                  : ''
              }
            >
              {celda}
            </CeldaStyled>
          );
        })}
      </tr>
    ))}
  </thead>
);

const Tbody = ({
  data,
  render,
  assets,
  cellTabindex,
  expanded,
  bodyCellProps,
}) => {
  const [isExpanded, setExpanded] = useState([]);
  const areExpanded = [];
  const handleExpand = idx => {
    areExpanded[idx] = !areExpanded[idx];
    setExpanded(areExpanded);
  };
  return (
    <tbody>
      {data.map((row, i) => {
        areExpanded.push(isExpanded[i] || false);
        return (
          <React.Fragment key={i}>
            <tr>
              {render(row).map((celda, j) => {
                const TagCelda =
                  assets?.primary === j + 1 && celda?.props?.children
                    ? 'th'
                    : 'td';
                return (
                  <CeldaStyled
                    key={j}
                    index={j}
                    assets={assets}
                    as={TagCelda}
                    tabIndex={cellTabindex ? 0 : undefined}
                    scope={TagCelda === 'th' ? 'row' : ''}
                    onClick={
                      assets?.expand === j + 1
                        ? () => handleExpand(i)
                        : undefined
                    }
                    {...bodyCellProps[j]}
                  >
                    {celda}
                  </CeldaStyled>
                );
              })}
            </tr>
            {isExpanded[i] && expanded && (
              <tr className="expanded">
                <td colSpan={render({}).length}>{expanded}</td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
};
const Tfoot = ({ tfootData, render, assets, cellTabindex }) => (
  <tfoot>
    {tfootData.map((row, i) => (
      <tr key={i}>
        {render(row).map((celda, j) => {
          const TagCelda =
            assets?.primary === j + 1 && celda?.props?.children ? 'th' : 'td';
          return (
            <CeldaStyled
              key={j}
              index={j}
              assets={assets}
              as={TagCelda}
              tabIndex={cellTabindex ? 0 : undefined}
              scope={TagCelda === 'th' ? 'row' : ''}
            >
              {celda}
            </CeldaStyled>
          );
        })}
      </tr>
    ))}
  </tfoot>
);

const GeneraTabla = ({
  caption,
  columns,
  data,
  tfootData,
  bodyCellProps = [{}, {}, {}, {}, {}],
  ...props
}) => (
  <TablaGeneralStyles {...props}>
    {caption && <caption>{caption}</caption>}
    {columns.hasOwnProperty('head') && (
      <Thead head={columns.head} assets={columns.assets} />
    )}
    <Tbody
      data={data}
      render={columns.render}
      assets={columns.assets}
      cellTabindex={props.cellTabindex}
      expanded={props.expanded}
      bodyCellProps={bodyCellProps}
    />
    {tfootData && (
      <Tfoot
        data={tfootData}
        render={columns.render}
        assets={columns.assets}
        cellTabindex={props.cellTabindex}
      />
    )}
  </TablaGeneralStyles>
);

export default GeneraTabla;

const CeldaStyled = styled.div`
  ${({ assets, index }) =>
    `text-align: center;
     width: ${sharedWidth(assets, index)};
     padding: ${sharedPadding(assets, index)};
  `}
  @media (max-width: ${responsive.tablet}px) {
    text-align: left;
  }
`;
